import { useMemo, type VoidFunctionComponent } from 'react';
import Subnav from '../generic/Subnav';
import { Action } from '../../../typings/permissions';
import { userHasPermissionForPolicyType } from '../../helpers/permissions';
import { window } from '../../globals';
import { useLocation } from '../../contexts/RouterContext';
import { useIsQualityEnabled, useIsSecurityEnabled, useIsSBOMPolicyEnabled } from '../../helpers/features';
import { PolicyType } from '../../../typings/enums';

//  Provides memoized checks for which nav items should be displayed
function usePoliciesSubnav() {
  const { pathname } = useLocation();
  const hasSecurity = useIsSecurityEnabled();
  const hasQuality = useIsQualityEnabled();
  const hasSBOM = useIsSBOMPolicyEnabled();

  return useMemo(() => ({
    activeSubNavKey: pathname.split('/').pop(),
    shouldShowLicensingPolicies: userHasPermissionForPolicyType(Action.View, PolicyType.LICENSING),
    shouldShowSecurityPolicies: hasSecurity && userHasPermissionForPolicyType(Action.View, PolicyType.SECURITY),
    shouldShowQualityPolicies: hasQuality && userHasPermissionForPolicyType(Action.View, PolicyType.QUALITY),
    shouldShowSBOMPolicies: hasSBOM && userHasPermissionForPolicyType(Action.View, PolicyType.SBOM),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This is fine and should ensure that this never re-runs unless something actually changes.
  }), [
    pathname,
    hasSecurity,
    hasQuality,
    window.user?.permissions,
  ]);
}

/**
 * PoliciesSubnav displays the three policy category options
 * in a standard Subnav component
 */
const PoliciesSubnav: VoidFunctionComponent = () => {
  const {
    activeSubNavKey,
    shouldShowLicensingPolicies,
    shouldShowSecurityPolicies,
    shouldShowQualityPolicies,
    shouldShowSBOMPolicies,
  } = usePoliciesSubnav();

  //  Note: I'm not entirely sure this would ever happen, but we guard against
  //  it in case, so that we don't render and empty subnav
  if (!shouldShowLicensingPolicies && !shouldShowSecurityPolicies
    && !shouldShowQualityPolicies && !shouldShowSBOMPolicies) {
    return null;
  }

  return (
    <Subnav activeKey={activeSubNavKey} variant="small">
      {shouldShowLicensingPolicies && (
        <Subnav.Item to="/policies/licensing" navKey="licensing">
          Licensing
        </Subnav.Item>
      )}
      {shouldShowSecurityPolicies && (
        <Subnav.Item to="/policies/security" navKey="security">
          Security
        </Subnav.Item>
      )}
      {shouldShowQualityPolicies && (
        <Subnav.Item to="/policies/quality" navKey="quality">
          Quality
        </Subnav.Item>
      )}
      {shouldShowSBOMPolicies && (
        <Subnav.Item to="/policies/sbom" navKey="sbom">
          SBOM
        </Subnav.Item>
      )}
    </Subnav>
  );
};

export default PoliciesSubnav;
